import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Zap, Plus, Trash2, Loader2, Mail, Copy, CheckCircle2, Flag, UserPlus, Tag as TagIcon, Bell, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { API } from "@/utils/constants";

const EMPTY_RULE = { name: '', trigger_type: 'column_moved', trigger_column_id: '', actions: { mark_complete: false, set_priority: null, assign_member_id: null, add_tag_id: null, notify_assignees: false } };

const ActionBadges = ({ actions, members, tags }) => {
  const items = [];
  if (actions.mark_complete) items.push({ icon: CheckCircle2, label: "Mark complete", cls: "bg-emerald-500/10 text-emerald-600 border-emerald-200" });
  if (actions.set_priority) items.push({ icon: Flag, label: `Priority: ${actions.set_priority}`, cls: "bg-orange-500/10 text-orange-600 border-orange-200" });
  if (actions.assign_member_id) { const m = members.find(x => x.id === actions.assign_member_id); items.push({ icon: UserPlus, label: `Assign: ${m?.name || 'member'}`, cls: "bg-blue-500/10 text-blue-600 border-blue-200" }); }
  if (actions.add_tag_id) { const t = tags.find(x => x.id === actions.add_tag_id); items.push({ icon: TagIcon, label: `Tag: ${t?.name || 'tag'}`, cls: "bg-purple-500/10 text-purple-600 border-purple-200" }); }
  if (actions.notify_assignees) items.push({ icon: Bell, label: "Notify assignees", cls: "bg-amber-500/10 text-amber-600 border-amber-200" });
  return (
    <div className="flex gap-1.5 flex-wrap">
      {items.map((it, i) => <Badge key={i} variant="outline" className={`text-xs ${it.cls}`}><it.icon className="h-3 w-3 mr-1" />{it.label}</Badge>)}
    </div>
  );
};

export const AutomationsView = ({ columns, members, tags }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [draft, setDraft] = useState(EMPTY_RULE);
  const [inbound, setInbound] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchRules = async () => {
    try {
      const [r, addr] = await Promise.all([axios.get(`${API}/automations`), axios.get(`${API}/inbound-email/address`)]);
      setRules(r.data);
      setInbound(addr.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchRules(); }, []);

  const saveRule = async () => {
    if (draft.trigger_type === 'column_moved' && !draft.trigger_column_id) { toast.error("Pick a trigger column"); return; }
    const hasAction = draft.actions.mark_complete || draft.actions.set_priority || draft.actions.assign_member_id || draft.actions.add_tag_id || draft.actions.notify_assignees;
    if (!hasAction) { toast.error("Pick at least one action"); return; }
    const colName = columns.find(c => c.id === draft.trigger_column_id)?.name || 'column';
    const name = draft.name.trim() || (draft.trigger_type === 'due_date_passed' ? 'When due date passes' : `When moved to ${colName}`);
    try {
      await axios.post(`${API}/automations`, { ...draft, name, trigger_column_id: draft.trigger_type === 'due_date_passed' ? null : draft.trigger_column_id });
      toast.success("Automation created");
      setShowModal(false);
      setDraft(EMPTY_RULE);
      fetchRules();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleRule = async (rule) => {
    try {
      await axios.put(`${API}/automations/${rule.id}`, { enabled: !rule.enabled });
      fetchRules();
    } catch { toast.error("Failed"); }
  };

  const deleteRule = async (id) => {
    try {
      await axios.delete(`${API}/automations/${id}`);
      toast.success("Automation deleted");
      fetchRules();
    } catch { toast.error("Failed"); }
  };

  const copyAddress = () => {
    navigator.clipboard.writeText(inbound?.address || '');
    setCopied(true);
    toast.success("Address copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const setAction = (key, value) => setDraft(d => ({ ...d, actions: { ...d.actions, [key]: value } }));

  if (loading) return <div className="p-6 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold mb-1 flex items-center gap-2" data-testid="automations-title">
            <Zap className="h-5 w-5 text-primary" />Automations
          </h2>
          <p className="text-sm text-muted-foreground">Rules that run when tasks move between columns</p>
        </div>
        <Button onClick={() => { setDraft(EMPTY_RULE); setShowModal(true); }} data-testid="new-automation-btn"><Plus className="h-4 w-4 mr-2" />New Rule</Button>
      </div>

      {rules.length === 0 ? (
        <div className="text-center py-12 bg-muted/30 rounded-xl border-2 border-dashed mb-8">
          <Zap className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-40" />
          <h3 className="font-semibold mb-1">No automations yet</h3>
          <p className="text-sm text-muted-foreground mb-4">Example: When a card moves to Done → mark complete & notify assignees</p>
          <Button variant="outline" onClick={() => { setDraft(EMPTY_RULE); setShowModal(true); }}><Plus className="h-4 w-4 mr-2" />Create your first rule</Button>
        </div>
      ) : (
        <div className="space-y-3 mb-8">
          {rules.map(rule => {
            const col = columns.find(c => c.id === rule.trigger_column_id);
            return (
              <div key={rule.id} className={`bg-card border rounded-xl p-4 transition-opacity ${rule.enabled ? '' : 'opacity-60'}`} data-testid={`automation-rule-${rule.id}`}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Zap className={`h-4 w-4 shrink-0 ${rule.enabled ? 'text-amber-500' : 'text-muted-foreground'}`} />
                    <span className="font-medium text-sm truncate">{rule.name}</span>
                    {rule.runs_count > 0 && <Badge variant="secondary" className="text-xs shrink-0">{rule.runs_count} run{rule.runs_count !== 1 ? 's' : ''}</Badge>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Switch checked={rule.enabled} onCheckedChange={() => toggleRule(rule)} data-testid={`automation-toggle-${rule.id}`} />
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteRule(rule.id)} data-testid={`automation-delete-${rule.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                  {rule.trigger_type === 'due_date_passed' ? (
                    <><span>When a task's</span><Badge variant="outline" className="text-xs bg-red-500/10 text-red-600 border-red-200">due date passes</Badge></>
                  ) : (
                    <><span>When task moves to</span><Badge variant="outline" className="text-xs">{col?.name || 'Unknown column'}</Badge></>
                  )}
                  <ArrowRight className="h-3 w-3" />
                </div>
                <ActionBadges actions={rule.actions || {}} members={members} tags={tags} />
              </div>
            );
          })}
        </div>
      )}

      <div className="bg-card border rounded-xl p-5" data-testid="email-to-task-card">
        <h3 className="font-semibold mb-1 flex items-center gap-2"><Mail className="h-4 w-4 text-primary" />Email-to-Task</h3>
        <p className="text-sm text-muted-foreground mb-3">Forward or send any email to this address and it becomes a task in this workspace (subject → title, body → description).</p>
        <div className="flex gap-2 mb-3">
          <Input readOnly value={inbound?.address || ''} className="font-mono text-sm" data-testid="inbound-email-address" />
          <Button variant="outline" onClick={copyAddress} data-testid="copy-inbound-address-btn">
            {copied ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer font-medium hover:text-foreground transition-colors">One-time setup required (Resend)</summary>
          <ol className="list-decimal ml-4 mt-2 space-y-1">
            <li>In Resend, add <span className="font-mono">operosmart.com</span> as a receiving domain (Domains → Add, enable receiving) and add the MX record it gives you to your DNS.</li>
            <li>Go to <span className="font-mono">resend.com/webhooks</span> → Add Webhook → URL: <span className="font-mono">https://operosmart.com/api/inbound-email/webhook</span> → event: <span className="font-mono">email.received</span>.</li>
            <li>Done — emails to the address above will appear as tasks in your first column.</li>
          </ol>
        </details>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Zap className="h-5 w-5 text-primary" />New Automation Rule</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Rule Name <span className="text-muted-foreground font-normal">(optional)</span></label>
              <Input placeholder="e.g., Done means done" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="automation-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Trigger</label>
              <Select value={draft.trigger_type} onValueChange={(v) => setDraft({ ...draft, trigger_type: v })}>
                <SelectTrigger data-testid="automation-trigger-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="column_moved">When a task moves to a column</SelectItem>
                  <SelectItem value="due_date_passed">When a task's due date passes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {draft.trigger_type === 'column_moved' && (
            <div>
              <label className="text-sm font-medium mb-1.5 block">When a task moves to…</label>
              <Select value={draft.trigger_column_id} onValueChange={(v) => setDraft({ ...draft, trigger_column_id: v })}>
                <SelectTrigger data-testid="automation-trigger-select"><SelectValue placeholder="Select column" /></SelectTrigger>
                <SelectContent>
                  {columns.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            )}
            {draft.trigger_type === 'due_date_passed' && (
              <p className="text-xs text-muted-foreground bg-muted/40 rounded-lg p-2.5">Checked automatically every 30 minutes. Runs once per overdue task. Assignees also get an in-app + email overdue alert.</p>
            )}
            <div className="space-y-3 pt-2 border-t">
              <label className="text-sm font-medium block">Then…</label>
              <div className="flex items-center gap-2">
                <Checkbox id="ac-complete" checked={draft.actions.mark_complete} onCheckedChange={(v) => setAction('mark_complete', !!v)} data-testid="action-mark-complete" />
                <label htmlFor="ac-complete" className="text-sm cursor-pointer">Mark task as complete</label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="ac-notify" checked={draft.actions.notify_assignees} onCheckedChange={(v) => setAction('notify_assignees', !!v)} data-testid="action-notify" />
                <label htmlFor="ac-notify" className="text-sm cursor-pointer">Notify assignees</label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Set priority</label>
                  <Select value={draft.actions.set_priority || 'none'} onValueChange={(v) => setAction('set_priority', v === 'none' ? null : v)}>
                    <SelectTrigger className="h-9" data-testid="action-priority-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Don't change</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Assign to</label>
                  <Select value={draft.actions.assign_member_id || 'none'} onValueChange={(v) => setAction('assign_member_id', v === 'none' ? null : v)}>
                    <SelectTrigger className="h-9" data-testid="action-assign-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nobody</SelectItem>
                      {members.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {tags.length > 0 && (
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Add tag</label>
                  <Select value={draft.actions.add_tag_id || 'none'} onValueChange={(v) => setAction('add_tag_id', v === 'none' ? null : v)}>
                    <SelectTrigger className="h-9" data-testid="action-tag-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No tag</SelectItem>
                      {tags.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={saveRule} data-testid="save-automation-btn"><Zap className="h-4 w-4 mr-2" />Create Rule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
